//LIBRERIES
import { computed, createApp, defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import { i18n } from '../lang/index';
import moment from "../node_modules/moment/moment";
import orderBy from "lodash/orderBy";
import deburr from "lodash/deburr";
import { useI18n } from 'vue-i18n';

//MODELS
import { App } from "../domain/App";
import { Publication } from "../domain/Publication";
import { Replacement } from "../domain/entities/replacement/Replacement";
import { Cookie } from "../domain/Cookie";
import { ProcessType } from "../domain/enum/ProcessType";
import { Languages } from "../domain/enum/Languages";
import { Constants } from "../domain/enum/Constants";
import ICommonDataState from '../domain/interfaces/ICommonDataState';

//APISERVICES
import { getReplacementLists } from "../infrastructure/apiServices/replacement.service";
import { getLastPublicationsList } from "../infrastructure/apiServices/publications.service";
import { getHeaderConfiguration, getClientAvailableLanguajes, getFeatureFlag } from "../infrastructure/apiServices/masterData.service";

//COMPOSABLES
import { useCommonData } from "./composables/useCommonData";
import { useIncidences } from "./composables/useIncidences";

export const ReplacementListComponent = defineComponent({
  setup() {
    // dependencies
    const { t, locale } = useI18n();

    // states
    const commonDataState = reactive({
      headerConfig: {},
      availableLanguajes: { defaultLanguage: '', secondLanguage: '' },
      cookie: new Cookie(),
      app: new App(),
      locale
    });

    // computed properties
    const pageTitle = computed(() => t('lang.shared.replacementLists'));
    const currentLanguage = computed(() => commonDataState.locale);
    const lastLogin = computed(() => moment(commonDataState.app.profile.lastLogin[0]).format(commonDataState.app.formatDate) + ' ' + moment.utc(commonDataState.app.profile.lastLogin[0]).local().format('HH:mm:ss'));

    // shared properties
    const imageClass = 'applicant-hero-image--replacement-list';
    const isLoading = ref(false);
    const showMandatoryCookies = ref(false);
    const showAnalysisCookies = ref(false);
    const showModalConfigurateCookies = ref(false);
    const showServiceStopModal = ref(false);
    const dontShowAgainStopServiceModalAgain = ref(false);
    const serviceStopLocalStorageKey = 'dontShowServiceStopModal';
    const serviceStopEndDate = moment('2024-05-15T17:00:00');

    // view properties
    const lastPublications = ref([]);
    const replacementList = ref([]);

    // methods
    const getLastPublicationsListOrdered = () => {
      lastPublications.value = orderBy(lastPublications.value, ['date', lastPublication => deburr(lastPublication.title[currentLanguage.value]).toLowerCase()], ['desc', 'asc']);
    };

    const getReplacementListOrdered = () => {
      replacementList.value = orderBy(replacementList.value, [replacement => deburr(replacement.name).toLowerCase(), 'creationDate'], ['asc', 'desc']);
    };

    const getData = () => {
      isLoading.value = true;
      getReplacementLists().then(function (response: any) {
        response.data.forEach(function (element: any) {
          const replacement = new Replacement(element);
          replacementList.value.push(replacement);
        });
        getReplacementListOrdered();
        isLoading.value = false;
      }).catch(function () {
        isLoading.value = false;
      });
    };

    const getAsideData = () => {
      isLoading.value = true;
      getLastPublicationsList(Constants.publications.AMOUNT_OF_PUBLICATIONS).then((response: { data: [] }) => {
        response.data.forEach(element => {
          lastPublications.value.push(new Publication(element));
        });
        getLastPublicationsListOrdered();
        isLoading.value = false;
      }).catch(() => {
        isLoading.value = false;
      });
    };

    const getAllData = () => {
      getData();
      getAsideData();
      commonDataState.app.getProfile();
    };

    const saveCookies = () => {
      commonDataState.cookie.savePersonalizateCookies();
      showModalConfigurateCookies.value = false;
    };

    const closeModalConfigurateCookies = () => {
      showModalConfigurateCookies.value = false;
    };

    const acceptAllCookies = () => {
      commonDataState.cookie.acceptAllCookies();
    };

    const declineAllCookies = () => {
      commonDataState.cookie.declineAllCookies();
    };

    const changeLocal = (language) => {
      commonDataState.locale = language;
      commonDataState.app.changeLanguaje(language);
      getReplacementListOrdered();
      getLastPublicationsListOrdered();
    };

    const goToDetail = (id: string) => {
      location.href = "replacementListDetail.html?id=" + id;
    };

    const goToJobProposal = (id: string, name: string) => {
      location.href = "replacementListJobProposal.html?id=" + id + '&name=' + name;
    };

    const updateCommonData = (state: ICommonDataState) => {
      commonDataState.availableLanguajes = state.availableLanguajes;
      commonDataState.cookie = state.cookie;
      commonDataState.headerConfig = state.headerConfig;
      commonDataState.app = state.app;
      commonDataState.locale = state.locale;

      getAllData();
    };

    // composables
    const useCommonDataDependencies = {
      updateCommonData,
      getHeaderConfiguration,
      getFeatureFlag,
      getClientAvailableLanguajes,
    };
    const { loadCommonData } = useCommonData(useCommonDataDependencies);
    const { incidence, showNotificationIncidence, IncidencesTypes, closeIncidence} = useIncidences();

    // lifecycle hooks
    onMounted(() => {
      loadCommonData();
      showServiceStopModal.value = commonDataState.app.isServiceStopActive && (!localStorage.getItem(serviceStopLocalStorageKey));
    });

    return {
      // dependencies
      t,
      ProcessType: ProcessType,
      Languages: Languages,
      // states
      ...toRefs(commonDataState),
      // shared view properties
      showServiceStopModal,
      showModalConfigurateCookies,
      dontShowAgainStopServiceModalAgain,
      serviceStopEndDate,
      showMandatoryCookies,
      showAnalysisCookies,
      imageClass,
      lastPublications,
      // computed properties
      lastLogin,
      pageTitle,
      currentLanguage,
      // methods
      getAllData,
      saveCookies,
      closeModalConfigurateCookies,
      acceptAllCookies,
      declineAllCookies,
      changeLocal,
      goToDetail,
      goToJobProposal,
      getData,
      getAsideData,
      getReplacementListOrdered,
      getLastPublicationsListOrdered,
      closeIncidence,
      // view properties
      isLoading,
      replacementList,
      incidence,
      showNotificationIncidence,
      IncidencesTypes
    }
  },
});

const vueApp = createApp(ReplacementListComponent);
vueApp.use(i18n);
vueApp.config.compilerOptions.whitespace = 'preserve';
vueApp.mount(Constants.APP);
